import { el } from './dom';
import { HeadPositionMap } from './HeadPositionMap';

export interface CalibrationStageCallbacks {
  /** Start the webcam (the click is the user gesture the browser needs). */
  onStart: () => void;
  /** Stop the webcam and leave tracking. */
  onStop: () => void;
  /** Capture the current pose as neutral. */
  onRecalibrate: () => void;
  /** Open the screen & distance calibration wizard. */
  onOpenWizard: () => void;
}

/**
 * The calibration stage.
 *
 * A small panel that can sit behind the (faded) menu: the blurred
 * head-position map drawn inside the camera frame, a status and viewer readout,
 * and the handful of buttons a setup needs. No camera image is shown — only the
 * map, so the effect is read against the camera edges rather than the picture.
 * The camera is opt-in: the panel opens in a standby state and only asks for the
 * webcam when Start is pressed, which is the gesture browsers require.
 */
export class CalibrationStage {
  readonly root = el('div', 'calibration-stage hidden');
  readonly map: HeadPositionMap;

  private readonly mapBox = el('div', 'cal-preview');
  private readonly canvas = el('canvas', 'cal-map');
  private readonly statusLine = el('p', 'cal-status', 'HEAD TRACKING OFF');
  private readonly viewerLine = el('p', 'cal-viewer', '');
  private readonly startButton = el('button', 'cal-button cal-start', 'Start camera');
  private readonly stopButton = el('button', 'cal-button cal-stop hidden', 'Stop camera');
  private readonly recalibrateButton = el('button', 'cal-button cal-recalibrate', 'Recalibrate');
  private readonly clearButton = el('button', 'cal-button cal-clear', 'Clear blur');
  private readonly wizardButton = el('button', 'cal-button cal-wizard-open', 'Calibrate screen');
  private tracking = false;

  constructor(
    parent: HTMLElement,
    private readonly callbacks: CalibrationStageCallbacks,
  ) {
    this.canvas.width = 240;
    this.canvas.height = 240;
    this.map = new HeadPositionMap(this.canvas);

    for (const node of [
      this.startButton,
      this.stopButton,
      this.recalibrateButton,
      this.clearButton,
      this.wizardButton,
    ]) {
      node.type = 'button';
    }
    this.startButton.addEventListener('click', () => this.callbacks.onStart());
    this.stopButton.addEventListener('click', () => this.callbacks.onStop());
    this.recalibrateButton.addEventListener('click', () => this.callbacks.onRecalibrate());
    this.clearButton.addEventListener('click', () => this.map.clear());
    this.wizardButton.addEventListener('click', () => this.callbacks.onOpenWizard());

    this.mapBox.append(this.canvas);

    const readout = el('div', 'cal-readout');
    readout.append(
      el('p', 'cal-title', 'CALIBRATION'),
      this.statusLine,
      this.viewerLine,
      el(
        'p',
        'cal-hint',
        'The square is the camera frame. Lean and watch the glow; the amber box is the dead zone, the dot is where the view goes.',
      ),
    );

    const controls = el('div', 'cal-controls');
    controls.append(
      this.startButton,
      this.stopButton,
      this.recalibrateButton,
      this.clearButton,
      this.wizardButton,
    );

    this.root.append(this.mapBox, readout, controls);
    parent.append(this.root);
    this.setTracking(false);
  }

  get visible(): boolean {
    return !this.root.classList.contains('hidden');
  }

  setVisible(visible: boolean): void {
    this.root.classList.toggle('hidden', !visible);
  }

  /** Show the standby or the running controls. */
  setTracking(tracking: boolean): void {
    this.tracking = tracking;
    this.startButton.classList.toggle('hidden', tracking);
    this.stopButton.classList.toggle('hidden', !tracking);
    this.recalibrateButton.disabled = !tracking;
    this.root.classList.toggle('tracking', tracking);
  }

  get isTracking(): boolean {
    return this.tracking;
  }

  setStatus(text: string, active: boolean): void {
    this.statusLine.textContent = text;
    this.statusLine.classList.toggle('active', active);
  }

  setViewer(text: string): void {
    this.viewerLine.textContent = text;
  }

  /** Feed one frame of calibration data. */
  sample(
    framePosition: { x: number; y: number } | null,
    screenX: number,
    screenY: number,
    now: number,
  ): void {
    this.map.sampleFrame(framePosition, now);
    this.map.setScreen(screenX, screenY);
  }

  render(now: number): void {
    this.map.render(now);
  }
}
